// src/pages/SettingsPage.tsx
import React, { useState } from 'react';
import { Box, Typography, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Divider, useTheme, useMediaQuery, CircularProgress, IconButton } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PaletteIcon from '@mui/icons-material/Palette';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LinkIcon from '@mui/icons-material/Link';
import MenuIcon from '@mui/icons-material/Menu'; // Import MenuIcon for mobile drawer toggle
import { useAppSelector } from '../app/hooks';
import { useTranslation } from 'react-i18next'; // Import useTranslation for i18n

// Import all settings panels
import ProfileSettings from '../features/settings/ProfileSettings';
import SecuritySettings from '../features/settings/SecuritySettings';
import AppearanceSettings from '../features/settings/AppearanceSettings';
import ConnectedAccountsSettings from '../features/settings/ConnectedAccountsSettings';
import NotificationsSettings from '../features/settings/NotificationsSettings';

const drawerWidth = 300;

const menuItems = [
    { id: 'profile', text: 'menu_profile', icon: <AccountCircleIcon /> }, // Updated to use translation key
    { id: 'security', text: 'menu_security', icon: <VpnKeyIcon /> }, // Updated to use translation key
    { id: 'appearance', text: 'menu_appearance', icon: <PaletteIcon /> }, // Updated to use translation key
    { id: 'connections', text: 'menu_connections', icon: <LinkIcon /> }, // Updated to use translation key
    { id: 'notifications', text: 'menu_notifications', icon: <NotificationsIcon /> }, // Updated to use translation key
];

const SettingsPage: React.FC = () => {
    const [selectedMenu, setSelectedMenu] = useState('profile');
    const [mobileOpen, setMobileOpen] = useState(false); // State to control mobile drawer
    const { user, status } = useAppSelector(state => state.auth);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation(); // Initialize translation hook

    // Handle drawer toggle for mobile
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const renderContent = () => {
        if (status.user === 'loading' && !user) {
            return <Box sx={{display: 'flex', justifyContent: 'center', p: 5}}><CircularProgress /></Box>;
        }
        if (!user) {
            return <Typography>{t('error_user_data')}</Typography>; // Use translation for error message
        }

        switch (selectedMenu) {
            case 'profile': return <ProfileSettings user={user} />;
            case 'security': return <SecuritySettings />;
            case 'appearance': return <AppearanceSettings user={user} />;
            case 'connections': return <ConnectedAccountsSettings />;
            case 'notifications': return <NotificationsSettings />;
            default: return <ProfileSettings user={user} />;
        }
    };

    const drawer = (
        <div>
            <Toolbar>
                <Typography variant="h6" noWrap component="div" sx={{ px: 1 }}>{t('settings')}</Typography> {/* Use translation for "Settings" */}
            </Toolbar>
            <Divider />
            <List sx={{ padding: 1 }}> {/* Added padding for menu items */}
                {menuItems.map(item => (
                    <ListItemButton
                        key={item.id}
                        selected={selectedMenu === item.id}
                        onClick={() => {
                            setSelectedMenu(item.id);
                            if (isMobile) setMobileOpen(false); // Close drawer on mobile after selection
                        }}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={t(item.text)} /> {/* Use translation for menu item text */}
                    </ListItemButton>
                ))}
            </List>
        </div>
    );

    return (
        <Box sx={{ display: 'flex', width: '100%', flexDirection: isMobile ? 'column' : 'row' }}>
            {/* Mobile drawer toggle button */}
            {isMobile && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', p: 2 }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2 }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ alignSelf: 'center' }}>{t('settings')}</Typography>
                </Box>
            )}
            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={isMobile ? mobileOpen : true} // Control open state for mobile
                onClose={isMobile ? handleDrawerToggle : undefined} // Handle close for mobile
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        position: 'relative',
                        boxShadow: 3, // Added shadow for drawer
                    },
                }}
            >
                {drawer}
            </Drawer>
            <Box component="main" sx={{ flexGrow: 1, p: 3, px: isMobile ? 3 : 10, py: 5 }}>
                {renderContent()}
            </Box>
        </Box>
    );
};

export default SettingsPage;